import React, { useState } from "react";
import { Field, FieldArray, getIn } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripLines } from "@fortawesome/pro-regular-svg-icons/faGripLines";
import { faTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { Error } from "@peracto/peracto-ui";

import { BRANCH_SEQUENCE_TOKENS } from "./constants";

const BranchSequenceList = ({ name, sequence, form, remove, label }) => {
    const [draggedIndex, setDraggedIndex] = useState(null);

    const moveItem = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) {
            return;
        }

        const next = [...sequence];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        form.setFieldValue(name, next);
    };

    const handleDragStart = (event, index) => {
        setDraggedIndex(index);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (event, dropIndex) => {
        event.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            return;
        }

        moveItem(draggedIndex, dropIndex);
        setDraggedIndex(null);
    };

    return (
        <ul className="list-group mb-2 p-0">
            {sequence.map((token, index) => (
                <li
                    key={token}
                    draggable
                    onDragStart={(event) => handleDragStart(event, index)}
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(event, index)}
                    onDragEnd={() => setDraggedIndex(null)}
                    className={`list-group-item d-flex align-items-center justify-content-between${
                        draggedIndex === index ? " opacity-50" : ""
                    }`}
                    style={{ cursor: "grab" }}
                    data-testid={`${name}-${index}-item`}
                >
                    <span className="d-flex align-items-center">
                        <span
                            className="mr-2 text-muted"
                            data-testid={`${name}-${index}-handle`}
                            aria-hidden="true"
                        >
                            <FontAwesomeIcon icon={faGripLines} />
                        </span>
                        <span className="badge badge-secondary mr-2">
                            {index + 1}
                        </span>
                        {token}
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-danger px-1"
                        aria-label={`Remove ${token}`}
                        data-testid={`${name}-${index}-remove`}
                        onClick={() => remove(index)}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </li>
            ))}
        </ul>
    );
};

export const BranchSequenceBuilder = ({ name, label = "Branch Sequence" }) => (
    <Field name={name}>
        {({ field, form }) => {
            const error = getIn(form.errors, name);
            const sequence = field.value || [];
            const available = BRANCH_SEQUENCE_TOKENS.filter(
                (token) => !sequence.includes(token)
            );

            return (
                <FieldArray name={name}>
                    {({ push, remove }) => (
                        <div className="mb-3" data-testid={`${name}-builder`}>
                            <label className="d-block mb-1">{label}</label>

                            {sequence.length > 0 ? (
                                <BranchSequenceList
                                    name={name}
                                    sequence={sequence}
                                    form={form}
                                    remove={remove}
                                />
                            ) : (
                                <p
                                    className="text-muted small mb-2"
                                    data-testid={`${name}-empty`}
                                >
                                    No branches added yet.
                                </p>
                            )}

                            {available.length > 0 && (
                                <div
                                    className="d-flex flex-wrap"
                                    style={{ gap: "0.5rem" }}
                                >
                                    {available.map((token) => (
                                        <button
                                            type="button"
                                            key={token}
                                            className="btn btn-sm btn-outline-secondary"
                                            data-testid={`${name}-add-${token}`}
                                            disabled={form.isSubmitting}
                                            onClick={() => push(token)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                className="mr-1"
                                            />
                                            {token}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {error && <Error error={error} name={label} />}
                        </div>
                    )}
                </FieldArray>
            );
        }}
    </Field>
);
